'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  IMessage,
  IReplyNavigationInitialFocus,
  useMessageContext,
} from '@ui/components/organisms/message';
import { ContextUserType } from '@ui/constants/context';
import PostItem from './post-item';
import { CommentList, ReplyFormPortal, Separator } from '@ui/components';
import { ReplyRefProvider } from '@ui/contexts';
import { PostItemSkeleton } from './post-item-skeleton';
import { MessageUpdateFormPortal } from '../common/message-update-form-portal';
import { MessageUpdateFormProvider } from '@ui/contexts/message-update-form-context';

interface Props {
  contextUserType: ContextUserType;
  message?: IMessage | null;
  initialFocus?: IReplyNavigationInitialFocus | null;
  isLoadingSingleMessage: boolean;
  isSingleMessageError: boolean;
  singleMessageError: any;
  useGetMessageByIdQuery: any;
  creatorData?: { platform_settings?: { enable_video_uploads?: boolean } };
  moderators_ids: string[];
  onPostViewed?: (resourceId: string) => void | Promise<void>;
  /** Dwell-driven highlight control threaded from the comment route. */
  highlight_visible?: boolean;
  highlight_auto_scroll?: boolean;
  /** Route generation used to re-arm scrolling for an explicit same-URL click. */
  highlight_generation_key?: string;
  onTargetVisibilityChange?: (visible: boolean) => void;
  onTargetLayoutChange?: () => void;
  onReplyTargetReadyChange?: (ready: boolean) => void;
  /** Explicit "See all comments" expansion grants one new highlight generation. */
  onSeeAllExpansion?: () => void;
}

type MessageQueryResult = {
  data?: IMessage;
  currentData?: IMessage;
  /** RTK Query fulfillment time for this argument, when available. */
  fulfilledTimeStamp?: number;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  error?: { status?: number } | unknown;
  refetch?: () => unknown;
};

const isAccessError = (error: unknown) =>
  error &&
  typeof error === 'object' &&
  'status' in error &&
  [401, 403, 404, 422].includes(Number((error as { status?: unknown }).status));

const isMessageForId = (message: IMessage | undefined, id: string | undefined) =>
  Boolean(message && id && (message.id === id || message.slug === id));

const selectContextMessage = ({
  seed,
  candidates,
  id,
  query,
  seedAcceptedAt,
  preferLive,
}: {
  seed?: IMessage;
  candidates: Array<IMessage | undefined>;
  id?: string;
  query: MessageQueryResult;
  seedAcceptedAt?: number;
  preferLive?: boolean;
}): IMessage | undefined => {
  if (query.isError && isAccessError(query.error)) return undefined;

  const liveCandidates = candidates.filter((candidate) => isMessageForId(candidate, id));
  const live = liveCandidates[0];
  const validSeed = isMessageForId(seed, id) && seed?.community_details ? seed : undefined;
  if (!live) return validSeed;
  if (!validSeed) return live;
  if (preferLive) return live;

  // A freshly authorized seed may be newer than a retained same-id cache row.
  // RTK's fulfillment timestamp is the request-level freshness signal:
  // message.updated_at does not change for viewer-specific votes/reactions.
  if (seedAcceptedAt !== undefined && query.fulfilledTimeStamp !== undefined) {
    return query.fulfilledTimeStamp > seedAcceptedAt ? live : validSeed;
  }

  // Keep a compatibility fallback for consumers/tests that do not expose RTK
  // timestamps yet. This is weaker than request freshness, but still avoids
  // replacing a newer seed with an older message snapshot.
  // Let the newer representation paint first. Equal message timestamps keep
  // the seed because viewer-specific cache fields may still be stale.
  const seedUpdatedAt = Date.parse(validSeed.updated_at || '');
  const liveUpdatedAt = Date.parse(live.updated_at || '');
  return Number.isFinite(seedUpdatedAt) && seedUpdatedAt >= liveUpdatedAt ? validSeed : live;
};

const ContextLoadError = ({ label, onRetry }: { label: string; onRetry?: () => unknown }) => (
  <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-3 py-8 text-center">
    <p className="text-sm text-korDarkGrey/60">Unable to load {label}.</p>
    {onRetry && (
      <button
        type="button"
        className="text-sm font-semibold text-primary hover:underline"
        onClick={() => void onRetry()}>
        Try again
      </button>
    )}
  </div>
);

export const PostViewV2 = (props: Props) => {
  const messageState = useMessageContext(props.contextUserType);
  const [expandedMessageId, setExpandedMessageId] = useState<string | null>(null);
  const { contextUserType, message, isSingleMessageError, onPostViewed } = props;
  const hasCompleteInitialFocus = Boolean(
    props.initialFocus?.completeness?.target &&
      props.initialFocus?.completeness?.mainPost &&
      props.initialFocus?.mainPost &&
      (props.initialFocus.targetType !== 'reply' ||
        (props.initialFocus.completeness.parentPost && props.initialFocus.parentPost)),
  );
  // A seed is presentation input only after the route validator has confirmed
  // root/parent completeness. An incomplete seed must never become an
  // interactive comment or reply context while the live lookups are pending.
  const initialFocus = hasCompleteInitialFocus ? props.initialFocus : null;
  const focusMessage = initialFocus?.target || props.message;

  useEffect(() => {
    if (contextUserType === 'fan' && message && !isSingleMessageError) {
      void onPostViewed?.(message.id);
    }
  }, [contextUserType, isSingleMessageError, message, onPostViewed]);

  const rootPostId = initialFocus?.mainPost?.id || focusMessage?.main_post?.id;
  const parentPostId = initialFocus?.parentPost?.id || focusMessage?.parent_post?.id;
  const isReply = focusMessage?.thread_deepness === 2;

  const rootPostQuery = props.useGetMessageByIdQuery(rootPostId || '', {
    skip: !rootPostId,
    ...(initialFocus ? { refetchOnMountOrArgChange: true } : {}),
  }) as MessageQueryResult;

  // A reply needs its parent comment to keep the context visible while the
  // full comment list is expanded. Do not issue an undefined request for a
  // top-level comment during a route transition.
  const parentPostQuery = props.useGetMessageByIdQuery(parentPostId || '', {
    skip: !isReply || !parentPostId,
    ...(initialFocus ? { refetchOnMountOrArgChange: true } : {}),
  }) as MessageQueryResult;
  const rootPostRefetch = rootPostQuery.refetch;
  const parentPostRefetch = parentPostQuery.refetch;

  // A mutation or realtime patch can replace a cached message object without
  // advancing RTK's query fulfillment timestamp. Track the live object for
  // this accepted seed generation so that a post-seed patch can reconcile
  // viewer-specific votes, reactions, and edits immediately.
  const rootLiveCandidate = [rootPostQuery.currentData, rootPostQuery.data].find((candidate) =>
    isMessageForId(candidate, rootPostId),
  );
  const parentLiveCandidate = [parentPostQuery.currentData, parentPostQuery.data].find(
    (candidate) => isMessageForId(candidate, parentPostId),
  );
  const rootLiveBaselineRef = useRef<{
    key: string;
    candidate?: IMessage;
    liveOwned: boolean;
  }>({
    key: '',
    candidate: undefined,
    liveOwned: false,
  });
  const parentLiveBaselineRef = useRef<{
    key: string;
    candidate?: IMessage;
    liveOwned: boolean;
  }>({
    key: '',
    candidate: undefined,
    liveOwned: false,
  });
  const rootLiveKey = `${rootPostId || ''}:${initialFocus?.generationId ?? 'live'}:${
    initialFocus?.acceptedAt ?? ''
  }`;
  const parentLiveKey = `${parentPostId || ''}:${initialFocus?.generationId ?? 'live'}:${
    initialFocus?.acceptedAt ?? ''
  }`;
  if (rootLiveBaselineRef.current.key !== rootLiveKey) {
    rootLiveBaselineRef.current = {
      key: rootLiveKey,
      candidate: rootLiveCandidate,
      liveOwned: false,
    };
  } else if (
    !rootLiveBaselineRef.current.liveOwned &&
    rootLiveCandidate !== undefined &&
    rootLiveBaselineRef.current.candidate !== undefined &&
    rootLiveCandidate !== rootLiveBaselineRef.current.candidate
  ) {
    rootLiveBaselineRef.current.liveOwned = true;
  } else if (
    rootLiveBaselineRef.current.candidate === undefined &&
    rootLiveCandidate !== undefined
  ) {
    rootLiveBaselineRef.current.candidate = rootLiveCandidate;
  }
  if (parentLiveBaselineRef.current.key !== parentLiveKey) {
    parentLiveBaselineRef.current = {
      key: parentLiveKey,
      candidate: parentLiveCandidate,
      liveOwned: false,
    };
  } else if (
    !parentLiveBaselineRef.current.liveOwned &&
    parentLiveCandidate !== undefined &&
    parentLiveBaselineRef.current.candidate !== undefined &&
    parentLiveCandidate !== parentLiveBaselineRef.current.candidate
  ) {
    parentLiveBaselineRef.current.liveOwned = true;
  } else if (
    parentLiveBaselineRef.current.candidate === undefined &&
    parentLiveCandidate !== undefined
  ) {
    parentLiveBaselineRef.current.candidate = parentLiveCandidate;
  }
  const rootLiveChangedAfterSeed =
    rootLiveBaselineRef.current.key === rootLiveKey && rootLiveBaselineRef.current.liveOwned;
  const parentLiveChangedAfterSeed =
    parentLiveBaselineRef.current.key === parentLiveKey && parentLiveBaselineRef.current.liveOwned;
  const contextRefetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialFocus?.acceptedAt || !rootPostId) return;
    const key = `${rootPostId}:${parentPostId || ''}:${initialFocus.generationId}:${
      initialFocus.acceptedAt
    }`;
    if (contextRefetchKeyRef.current === key) return;
    contextRefetchKeyRef.current = key;
    void rootPostRefetch?.();
    if (isReply && parentPostId) void parentPostRefetch?.();
  }, [
    initialFocus?.acceptedAt,
    initialFocus?.generationId,
    isReply,
    parentPostId,
    parentPostRefetch,
    rootPostId,
    rootPostRefetch,
  ]);

  // `data` can be retained from the previous query while a notification moves
  // from reply A to reply B. Prefer currentData and accept retained data only
  // when its id still matches this route's required context.
  const rootPost = selectContextMessage({
    seed: initialFocus?.mainPost,
    candidates: [rootPostQuery.currentData, rootPostQuery.data],
    id: rootPostId,
    query: rootPostQuery,
    seedAcceptedAt: initialFocus?.acceptedAt,
    preferLive: rootLiveChangedAfterSeed,
  });
  const parentPost = selectContextMessage({
    seed: initialFocus?.parentPost,
    candidates: [parentPostQuery.currentData, parentPostQuery.data],
    id: parentPostId,
    query: parentPostQuery,
    seedAcceptedAt: initialFocus?.acceptedAt,
    preferLive: parentLiveChangedAfterSeed,
  });
  const comment = useMemo(
    () => (isReply ? parentPost : props.message || undefined),
    [isReply, parentPost, props.message],
  );

  if (props.isLoadingSingleMessage) {
    return (
      <div className="mx-auto flex w-full max-w-[876px]">
        <PostItemSkeleton />
      </div>
    );
  }

  if (props.isSingleMessageError) {
    return (
      <div className="m-auto max-w-sm">
        <div className="flex flex-col justify-center space-y-4">
          <div className="text-center">
            <p className="font-nunito text-korDarkGrey text-3xl font-extrabold">
              {props.singleMessageError?.status === 403
                ? 'Oops, post is restricted.'
                : props.singleMessageError?.status === 422
                ? 'Content Removed'
                : 'Something went wrong'}
            </p>
            <p className="font-nunito text-korDarkGrey/40">
              {props.singleMessageError?.status === 422
                ? 'This content is no longer available.'
                : props.singleMessageError?.data?.error || 'Error loading post...'}
            </p>
          </div>
          <Separator className="" />
        </div>
      </div>
    );
  }

  if (!focusMessage) {
    return <div>No post found</div>;
  }

  const showAllComments = expandedMessageId === focusMessage.id;
  // Comments and replies need their root post before we can safely render the
  // discussion entry point. A legacy notification can omit `main_post`, so do
  // not render a parent comment and "See all comments" without that context.
  const needsRootContext = focusMessage.thread_deepness > 0;
  const missingRootContext = Boolean((rootPostId || needsRootContext) && !rootPost);
  const missingParentContext = Boolean(isReply && parentPostId && !parentPost);
  const contextQuery = missingRootContext ? rootPostQuery : parentPostQuery;

  if (missingRootContext || missingParentContext) {
    if (contextQuery.isLoading || contextQuery.isFetching) {
      return (
        <div className="mx-auto flex w-full max-w-[876px]">
          <PostItemSkeleton />
        </div>
      );
    }

    return (
      <ContextLoadError
        label={missingParentContext ? 'the comment context' : 'the post context'}
        onRetry={missingRootContext && !rootPostId ? undefined : contextQuery.refetch}
      />
    );
  }

  return (
    <MessageUpdateFormProvider>
      <ReplyRefProvider>
        <>
          <MessageUpdateFormPortal
            userType={props.contextUserType}
            main_post_ref={(rootPost?.id || rootPostId || focusMessage.id) as string}
          />

          <div className="mx-auto flex w-full max-w-[876px] flex-col gap-4 pb-20">
            {/* root post */}
            {rootPost && (
              <PostItem
                key={rootPost?.id}
                post={rootPost}
                userType={props.contextUserType}
                userId={messageState.user_id}
                creator_id={rootPost.creator_id}
                communityPostRoute={messageState.communityPostRoute}
                isOwnPage
                query={messageState.query}
                contentType="post"
                isDynamicRoute={false}
                communityDetails={
                  focusMessage.community_details as NonNullable<IMessage['community_details']>
                }
                moderators_ids={props.moderators_ids}
              />
            )}

            {comment && !showAllComments && (
              <div className="flex items-center gap-4">
                <Separator className="flex-auto bg-primaryActive" />
                <button
                  type="button"
                  className="text-xs whitespace-nowrap text-primaryActive"
                  onClick={() => {
                    props.onSeeAllExpansion?.();
                    setExpandedMessageId(focusMessage.id);
                  }}
                  aria-label="See all comments"
                  aria-expanded={showAllComments}>
                  See all comments
                </button>
                <Separator className="flex-auto bg-primaryActive" />
              </div>
            )}

            {/* {!rootPost?.draft && <p className="font-nunito text-sm font-semibold">Comments</p>} */}

            {comment && (
              <ReplyFormPortal
                userType={props.contextUserType}
                creator_id={comment.creator_id}
                user_id={messageState.user_id}
                main_post_ref={rootPost?.id || rootPostId || focusMessage.id}
                queryParams={messageState.query}
                community_details={
                  comment.community_details as NonNullable<IMessage['community_details']>
                }
              />
            )}

            {rootPost && !rootPost?.draft && comment && (
              <ReplyRefProvider>
                <CommentList
                  contentType="comment"
                  topLevelComments
                  initialFocus={initialFocus}
                  userType={props.contextUserType}
                  post={rootPost}
                  creator_id={rootPost.community_details?.creator_id as string}
                  user_id={messageState.user_id}
                  main_post_ref={rootPost?.id || rootPostId || focusMessage.id}
                  enableReply={true}
                  isDynamicRoute={false}
                  filter_by_comment_id={showAllComments ? undefined : comment.id}
                  {...(focusMessage.thread_deepness === 2 && {
                    filter_by_reply_id: showAllComments ? undefined : focusMessage.id,
                  })}
                  target_comment_id={comment.id}
                  target_reply_id={isReply ? focusMessage.id : undefined}
                  highlight_visible={props.highlight_visible}
                  highlight_auto_scroll={props.highlight_auto_scroll}
                  highlight_generation_key={props.highlight_generation_key}
                  onTargetVisibilityChange={props.onTargetVisibilityChange}
                  onTargetLayoutChange={props.onTargetLayoutChange}
                  onReplyTargetReadyChange={props.onReplyTargetReadyChange}
                  creatorData={props.creatorData}
                  moderators_ids={props.moderators_ids || []}
                />
              </ReplyRefProvider>
            )}
          </div>
        </>
      </ReplyRefProvider>
    </MessageUpdateFormProvider>
  );
};
